import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { TipAllocationModeEnum } from '@/gen/cortado/v1/settings_pb'

import { normalizeTipAllocationMode } from './tip-allocation-mode'

interface TipAllocationModeSelectProps {
  value?: TipAllocationModeEnum
  onChange: (value: TipAllocationModeEnum) => void
  disabled?: boolean
  /** Shown as muted helper text, typically when `disabled` is set. */
  disabledReason?: string
  label?: string
  id?: string
}

const OPTIONS = [
  {
    value: TipAllocationModeEnum.TIP_ALLOCATION_MODE_LAST_SCHEDULE,
    label: 'Final payment',
    description: 'The whole tip is collected with the last payment.',
  },
  {
    value: TipAllocationModeEnum.TIP_ALLOCATION_MODE_PRO_RATA,
    label: 'Split across payments',
    description: 'Each payment collects a share of the tip in proportion to its amount.',
  },
] as const

/**
 * Chooses whether a document's tip is collected on the final payment or split
 * across payment schedules.
 */
export function TipAllocationModeSelect({
  value,
  onChange,
  disabled = false,
  disabledReason,
  label = 'Tip collection',
  id = 'tip-allocation-mode',
}: TipAllocationModeSelectProps) {
  const normalized = normalizeTipAllocationMode(value)
  const selected = OPTIONS.find((option) => option.value === normalized) ?? OPTIONS[0]

  return (
    <div className="grid min-w-0 gap-2">
      <Label htmlFor={id}>{label}</Label>
      <Select
        value={String(normalized)}
        onValueChange={(next) => onChange(Number(next) as TipAllocationModeEnum)}
        disabled={disabled}
      >
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder="Select tip collection" />
        </SelectTrigger>
        <SelectContent>
          {OPTIONS.map((option) => (
            <SelectItem key={option.value} value={String(option.value)}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-xs text-muted-foreground">
        {disabled && disabledReason ? disabledReason : selected.description}
      </p>
    </div>
  )
}
